import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { CheckCircle2, XCircle, ArrowRight, Hourglass } from "lucide-react";

// Resolve where to send the user after a successful (or pending) payment, based on context.
function destinationFor(payment) {
  if (!payment) return "/dashboard";
  const { context_type, context_id } = payment;
  switch (context_type) {
    case "lms":          return `/dashboard/courses/${context_id}/learn`;
    case "guidance":     return `/dashboard/guidance/${context_id}`;
    case "publication":  return `/dashboard/ideas/${context_id}`;
    case "subscription": return "/institution";
    case "consultation": return "/dashboard";
    case "event":        return `/events`;
    default:             return "/dashboard";
  }
}

function destinationLabel(payment) {
  if (!payment) return "Go to dashboard";
  switch (payment.context_type) {
    case "lms":          return "Start learning";
    case "guidance":     return "Open guidance workspace";
    case "publication":  return "Open project";
    case "subscription": return "Go to institution dashboard";
    case "event":        return "Back to events";
    default:             return "Go to dashboard";
  }
}

export default function PaymentReturn() {
  const [params] = useSearchParams();
  const provider = params.get("provider");
  const pidx = params.get("pidx");
  const purchase_order_id = params.get("purchase_order_id") || params.get("transaction_uuid");
  const [state, setState] = useState({ status: "verifying" });
  const [autoCountdown, setAutoCountdown] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const run = async () => {
      try {
        if (provider === "khalti" && pidx) {
          const r = await api.get(`/payments/khalti/verify`, { params: { pidx } });
          setState({ status: r.data.payment.status, payment: r.data.payment });
        } else if (provider === "esewa" && purchase_order_id) {
          const r = await api.get(`/payments/esewa/verify`, { params: { purchase_order_id } });
          setState({ status: r.data.payment.status, payment: r.data.payment });
        } else if (provider === "manual_qr" && purchase_order_id) {
          // Manual QR doesn't auto-verify but we still know the context from the saved kb_return_to
          let payment = null;
          try {
            const cached = sessionStorage.getItem("kb_payment_context");
            if (cached) payment = JSON.parse(cached);
          } catch { /* ignore */ }
          setState({ status: "awaiting_manual_confirmation", payment });
        } else {
          setState({ status: "unknown" });
        }
      } catch (e) {
        setState({ status: "error", error: e?.response?.data?.detail || "Verification failed" });
      }
    };
    run();
  }, [provider, pidx, purchase_order_id]);

  const dest = useMemo(() => destinationFor(state.payment), [state.payment]);
  const label = useMemo(() => destinationLabel(state.payment), [state.payment]);
  const completed = state.status === "completed";
  const failed = ["failed", "expired", "user_canceled", "error"].includes(state.status);

  // Auto-redirect 5s after successful completion
  useEffect(() => {
    if (!completed) return;
    setAutoCountdown(5);
    const tick = setInterval(() => {
      setAutoCountdown((s) => {
        if (s === null) return null;
        if (s <= 1) { clearInterval(tick); navigate(dest); return null; }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(tick);
  }, [completed, dest, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-kb-bg px-6" data-testid="payment-return-page">
      <div className="max-w-md w-full text-center">
        {state.status === "verifying" && (
          <>
            <Hourglass className="w-10 h-10 text-kb-primary mx-auto" strokeWidth={1.5} />
            <h2 className="font-serif text-3xl text-kb-ink mt-4">Verifying payment…</h2>
            <p className="text-kb-ink-muted mt-2">This usually takes a few seconds.</p>
          </>
        )}
        {completed && (
          <>
            <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto" strokeWidth={1.5} />
            <h2 className="font-serif text-3xl text-kb-ink mt-4">Payment received</h2>
            <p className="text-kb-ink-muted mt-2">
              Your access has been activated. {autoCountdown !== null && <>Redirecting in {autoCountdown}s…</>}
            </p>
            <Link to={dest} className="kb-btn-primary mt-6 inline-flex" data-testid="payment-return-continue-btn">
              {label} <ArrowRight className="w-4 h-4" />
            </Link>
            <div className="mt-3">
              <Link to="/dashboard" className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted hover:text-kb-primary">
                or go to dashboard
              </Link>
            </div>
          </>
        )}
        {state.status === "awaiting_manual_confirmation" && (
          <>
            <Hourglass className="w-12 h-12 text-amber-500 mx-auto" strokeWidth={1.5} />
            <h2 className="font-serif text-3xl text-kb-ink mt-4">Awaiting admin confirmation</h2>
            <p className="text-kb-ink-muted mt-2">Your QR payment is being verified. You'll regain access right after.</p>
            <Link to={dest} className="kb-btn-outline mt-6 inline-flex">Back to {label.toLowerCase().replace(/^(start |open |go to )/, "")}</Link>
          </>
        )}
        {failed && (
          <>
            <XCircle className="w-12 h-12 text-rose-500 mx-auto" strokeWidth={1.5} />
            <h2 className="font-serif text-3xl text-kb-ink mt-4">Payment not completed</h2>
            <p className="text-kb-ink-muted mt-2">{state.error || "Status: " + state.status}</p>
            <button onClick={() => navigate(-1)} className="kb-btn-outline mt-6">Try again</button>
          </>
        )}
      </div>
    </div>
  );
}
